import {ActionContext} from "vuex";
import {IAppState} from "@/interfaces/app-state.interface";
import {Vue} from "vue-property-decorator";
import {httpResource} from "@/resources/http.resource";
import {IApp} from "@/interfaces/app.interface";
import {ICarStore} from "@/modules/Car/interfaces/car-store.interface";
import {ICarList} from "@/modules/CarList/interfaces/car-list.interface";
import {CarInfoConfig} from "@/modules/Car/configs/car-info-config";
import {IBook} from "@/interfaces/book.interface";
import {IAuth} from "@/modules/Auth/interfaces/auth.interface";
import {bus} from "@/plugins/bus";
import {ICarRequest} from "@/modules/Car/interfaces/car-request.interface";
import {ICarPopupTables} from "@/modules/Car/interfaces/car-popup-tables.interface";

type CarStoreType = ActionContext<ICarStore.IState, IAppState>;

export const CarStore: ICarStore.ICarStoreModule = {
  namespaced: true,

  state: {
    activeCar: null,
    displayPopup: false,
    displayContent: true,
    popupPayload: null,
    createMode: false,
    tables: null,

    formData: {
      defaultValues: {...CarInfoConfig.DefaultFormModel},
      formModel: {},
      startForm: {},
    },

    loading: false,
  },
  mutations: {
    setProperty(state: ICarStore.IState, { name, value }: { name: string, value: any}) { Vue.set(state, name, value); },
    setSubProperty(state: ICarStore.IState, { block, name, value }: { block: string, name: string, value: any}) { Vue.set(state[block], name, value); },
    startLoading(state: ICarStore.IState) { state.loading = true },
    stopLoading(state: ICarStore.IState) { state.loading = false },

    setFormProperty(state: ICarStore.IState, { formName, name, value }: { formName: string, name: string, value: any}) { Vue.set(state.formData[formName], name, value); },
    setTableInCarPopup(state: ICarStore.IState, { tableName, array }: { tableName: ICarPopupTables.CarPopupTables, array: any[] }) {
      if (!state.tables) state.tables = {};
      Vue.set(state.tables, tableName, array[tableName]);
    },

    clearTableInCarPopup(state: ICarStore.IState) {
      if (!state.tables) state.tables = {};
      Vue.set(state, 'tables', null);
    },
  },

  getters: {
    carInfoBookCorps: (state: ICarStore.IState, getters, rootState: IAppState): IBook.CorpBookItem[] => {
      let arr = rootState.book.books.corps;
      const systemValues = [100000, 100001];
      const filteredArr = [];
      let remainingToFind = systemValues.length;

      for (const item of arr) {
        if (remainingToFind > 0 && systemValues.includes(item.id)) {
          remainingToFind--;
          continue;
        }
        filteredArr.push(item);

        if (remainingToFind === 0) {
          // Add remaining items without checking
          const currentIndex = arr.indexOf(item);
          filteredArr.push(...arr.slice(currentIndex + 1));
          break;
        }
      }

      return filteredArr;
    }
  },

  actions: {
    async openCarPopup(store: CarStoreType, payload: ICarStore.IPopupPayload) {
      store.commit('setProperty', { name: 'displayContent', value: true, });
      store.commit('setProperty', { name: 'popupPayload', value: payload, });
      store.commit('clearTableInCarPopup');

      store.commit('setProperty', { name: 'displayPopup', value: true, });
    },

    async closeCarPopup(store: CarStoreType) {
      store.commit('clearTableInCarPopup');
      store.commit('setProperty', { name: 'displayContent', value: false, });
      store.commit('setSubProperty', { block: 'formData', name: 'formModel', value: {...store.state.formData.defaultValues} });
      store.commit('setProperty', { name: 'activeCar', value: null });
      store.commit('setProperty', { name: 'createMode', value: false });

      if (store.state.popupPayload.closeAction) store.state.popupPayload.closeAction();

      store.commit('setProperty', { name: 'popupPayload', value: null, });
      store.commit('setProperty', { name: 'displayPopup', value: false, });
    },

    async getCar(store: CarStoreType) {
      store.commit('startLoading');

      if ((!store.state.popupPayload.carNumber && !store.state.popupPayload.carId) || store.state.popupPayload.createMode) {
        store.commit('setProperty', { name: 'createMode', value: true });
      }

      let asyncAction = async () => {
        const action = ICarRequest.Action.CarsMK;
        const params: any = {
          command: ICarRequest.Command.Get,
        };
        if (store.state.popupPayload.carNumber) params.fltStr = store.state.popupPayload.carNumber;
        else if (store.state.popupPayload.carId) params.carId = store.state.popupPayload.carId;

        const { data: { result: response }} = await httpResource.httpAction({ action, params });
        const carList = ICarList.PrepareCarList(store.rootState, response.Cars);
        if (carList.length) store.commit('setProperty', { name: 'activeCar', value: carList[0] });
      };

      if (!store.state.createMode) {
        await store.dispatch('defaultResourceAction', { asyncAction }, { root: true });
      }
      store.commit('stopLoading');
    },

    async prepareCarInfo(store: CarStoreType) {
      store.commit('setSubProperty', {
        block: 'formData',
        name: 'defaultValues',
        value: {
          ...CarInfoConfig.DefaultFormModel,
          guestFlag: Boolean(store.state.popupPayload.isGuest),
        },
      });

      let formModel = {...store.state.formData.defaultValues};
      let startForm = {...store.state.formData.defaultValues};
      if (!store.state.createMode) {
        if (store.state.activeCar && store.state.activeCar.corpId) {
          store.state.activeCar.corpId = +store.state.activeCar.corpId;
        }
        formModel = store.state.activeCar ? { ...store.state.activeCar } : {...store.state.formData.defaultValues};
        startForm = store.state.activeCar ? { ...store.state.activeCar } : {...store.state.formData.defaultValues};

        if (store.state.activeCar && store.state.activeCar.payGuestHours === 0) {
          formModel.payGuestHours = startForm.payGuestHours = null;
        }
      }

      const parkValues = store.rootState.book.books[IBook.BookName.parks];
      const tarifs = store.rootState.book.books[IBook.BookName.tarifs];
      if (parkValues.length === 1 && store.state.createMode) formModel.parks = [parkValues[0].id];
      if (tarifs.length === 1) formModel.parks = [tarifs[0].id];

      const userAccess: IAuth.UserAccess = store.rootState.auth.userAccess;
      if (store.state.createMode) {
        formModel.payGuestHours = userAccess.payGuestHours;
        startForm.payGuestHours = userAccess.payGuestHours;

        formModel.corpId = store.getters.carInfoBookCorps.length === 1 ? store.getters.carInfoBookCorps[0].id : null;
        if (!userAccess.corpId) {
          formModel.parks = parkValues.map(park => park.id);
        }

        if (store.state.popupPayload.createMode && store.state.popupPayload.presetFields) {
          store.state.popupPayload.presetFields.forEach(field => {
            formModel[field.prop] = field.value;
          })
        }
      }

      store.commit('setSubProperty', { block: 'formData', name: 'formModel', value: formModel });
      store.commit('setSubProperty', { block: 'formData', name: 'startForm', value: startForm });
    },

    async saveCarData(store: CarStoreType, { car, command, needUpdate }: ICarStore.ISaveCarPayload) {
      store.commit('startLoading');
      let asyncAction = async () => {
        const action = ICarRequest.Action.CarsMK;
        const params: any = {
          command: command === ICarRequest.Command.Delete && needUpdate ? ICarRequest.Command.Update : command,
          ...car,
          extModeFlag: store.rootState.auth.userAccess.extModeFlag,
        };
        const {data: {result: { Cars: carList }}} = await httpResource.httpAction({ action, params });
        if (carList && carList.length) {
          if (command === ICarRequest.Command.Add) store.commit('carList/addCarInList', carList[0], { root: true });
          if (command === ICarRequest.Command.Update) {
            store.commit('setProperty', { name: 'activeCar', value: carList[0] });
            store.commit('carList/updateCarInList', {
              car: carList[0],
              carListPage: store.rootState.route.name === IApp.ROUTE_NAME.PAGE_CAR_LIST
            }, { root: true });
          }

          if (carList[0].corpId && carList[0].corpName) {
            let corpInBook = store.rootState.book.books[IBook.BookName.Corps].find(corp => corp.id === +carList[0].corpId);
            if (!corpInBook) {
              store.commit('book/addItemInBook', {
                bookName: IBook.BookName.Corps,
                item: {
                  id: +carList[0].corpId,
                  name: carList[0].corpName,
                },
              }, { root: true });
            }
          }
        }
        if (command === ICarRequest.Command.Delete) {
          if (needUpdate) await httpResource.httpAction({ action, params: { command, carId: car.carId } });
          store.commit('carList/removeCarFromList', car.carId, { root: true });
        }
        if (command === ICarRequest.Command.Add) {
          bus.$emit(ICarList.EmitterEvents.CREATE_CAR, carList[0]);
        }
      };
      let response = await store.dispatch('defaultResourceAction', { asyncAction }, { root: true });
      store.commit('stopLoading');
      return response.success;
    },

    async getTable(store: CarStoreType, params: ICarPopupTables.ICarTableParams) {
      store.commit('startLoading');
      let asyncAction = async () => {
        const action = ICarRequest.Action.CarsMK;
        const {data: {result: response }} = await httpResource.httpAction({ action, params });
        store.commit('setTableInCarPopup', {
          tableName: ICarPopupTables.CarPopupTableArrayName[params.command],
          array: response,
        });
      };
      await store.dispatch('defaultResourceAction', { asyncAction }, { root: true });
      store.commit('stopLoading');
    },
  }
};
